import { useEffect, useState } from "react";
import { Plus, Trash2, Upload, FileText, X, BadgeDollarSign } from "lucide-react";
import { toast } from "sonner";
import { api, usd, errMsg, fileUrl } from "@/lib/api";
import { PageTitle } from "./ResourcePage";

const box = "h-10 w-full min-w-0 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF3333]/30";
const fmtAt = (s) => (s ? new Date(s).toLocaleString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const Field = ({ label, children }) => <label className="block space-y-1 min-w-0"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>{children}</label>;

const Txt = ({ row, k, label, type = "text", onSave }) => {
  const [v, setV] = useState(row[k] ?? "");
  const cur = row[k];
  useEffect(() => { setV(cur ?? ""); }, [cur]);
  return <Field label={label}><input type={type} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => String(v) !== String(row[k] ?? "") && onSave({ [k]: v })} className={box} data-testid={`sale-${k}-${row.id}`} /></Field>;
};

const Pick = ({ row, k, label, opts, onSave }) => (
  <Field label={label}><select value={row[k] || opts[0][0]} onChange={(e) => onSave({ [k]: e.target.value })} className={box} data-testid={`sale-${k}-${row.id}`}>
    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
);

function Docs({ row, onSave }) {
  const [busy, setBusy] = useState(false);
  const docs = row.documents || [];
  const add = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    setBusy(true);
    const up = [];
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("visibility", "private");
      try { const r = await api.post("/admin/upload", fd); up.push({ name: r.data.name, path: r.data.path, url: r.data.url, at: new Date().toISOString() }); } catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
    }
    setBusy(false);
    if (up.length) { await onSave({ documents: [...docs, ...up] }); toast.success(`${up.length} documento(s) guardado(s)`); }
  };
  const remove = (i) => window.confirm(`¿Quitar "${docs[i].name}"?`) && onSave({ documents: docs.filter((_, j) => j !== i) });
  return (
    <div className="space-y-2" data-testid={`sale-docs-${row.id}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Documentos ({docs.length})</p>
      {docs.map((d, i) => (
        <div key={i} className="flex items-center gap-3 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`sale-doc-${row.id}-${i}`}>
          <FileText size={15} className="text-[#FF3333] shrink-0" />
          <div className="flex-1 min-w-0 sm:flex sm:items-center sm:gap-3">
            <a href={`${fileUrl(d.url, true)}&download=1`} className="block truncate hover:underline sm:flex-1 min-w-0">{d.name}</a>
            <span className="block text-xs text-slate-500 whitespace-nowrap">Subido: {fmtAt(d.at)}</span>
          </div>
          <button onClick={() => remove(i)} className="p-2.5 -m-1 text-slate-400 hover:text-red-600" data-testid={`sale-doc-remove-${row.id}-${i}`}><X size={15} /></button>
        </div>
      ))}
      <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer rounded-full border border-dashed border-slate-300 px-4 py-2 hover:border-[#FF3333] hover:text-[#FF3333]">
        <Upload size={14} />{busy ? "Subiendo..." : "Subir documentos"}
        <input type="file" multiple className="hidden" onChange={add} disabled={busy} data-testid={`sale-doc-input-${row.id}`} />
      </label>
    </div>
  );
}

const Contact = ({ title, pre, r, s }) => (
  <div className="rounded-xl bg-slate-50 p-4 space-y-3" data-testid={`sale-${pre}-box-${r.id}`}>
    <p className="font-semibold text-sm">{title}</p>
    <div className="grid gap-4 sm:grid-cols-3">
      <Txt row={r} k={pre === "owner" ? "owner" : "buyer_name"} label="Nombre" onSave={s} />
      <Txt row={r} k={`${pre}_phone`} label="Teléfono" type="tel" onSave={s} />
      <Txt row={r} k={`${pre}_email`} label="Email" type="email" onSave={s} />
    </div>
  </div>
);

function SaleCard({ r, n, save, del }) {
  const s = (p) => save(r.id, p);
  return (
    <article className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-5" data-testid={`sale-card-${r.id}`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-bold text-lg truncate"><span className="text-slate-400 mr-2">#{n}</span>{r.property || "Nueva venta"}</h3>
        <button onClick={() => del(r.id)} className="p-3 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`sale-delete-${r.id}`}><Trash2 size={16} /></button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2"><Txt row={r} k="property" label="Propiedad" onSave={s} /></div>
        <Txt row={r} k="sector" label="Sector" onSave={s} />
        <Pick row={r} k="condition" label="Estado" opts={[["listo", "Lista"], ["construccion", "En construcción"]]} onSave={s} />
        <Txt row={r} k="price" label="Precio (US$)" type="number" onSave={s} />
        <Txt row={r} k="reservation" label="Reserva (US$)" type="number" onSave={s} />
        <Txt row={r} k="commission_pct" label="% de comisión" type="number" onSave={s} />
        <Field label="Mi comisión (US$)"><div className="h-10 rounded-lg bg-emerald-50 border border-emerald-200 px-3 flex items-center font-mono-num font-semibold text-emerald-700" data-testid={`sale-commission-${r.id}`}>{usd(r.commission_total)}</div></Field>
        <Txt row={r} k="sale_date" label="Fecha de venta" type="date" onSave={s} />
        <Txt row={r} k="closing_date" label="Fecha de cierre" type="date" onSave={s} />
        <Pick row={r} k="agent_mode" label="¿Solo o con otro agente?" opts={[["solo", "Solo"], ["otro", "Con otro agente"]]} onSave={s} />
        {r.agent_mode === "otro" && <Txt row={r} k="other_agent" label="Nombre del otro agente" onSave={s} />}
      </div>
      <Contact title="Propietario" pre="owner" r={r} s={s} />
      <Contact title="Comprador" pre="buyer" r={r} s={s} />
      <Docs row={r} onSave={s} />
    </article>
  );
}

const Stat = ({ l, v, tid }) => <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={tid}><p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">{l}</p><p className="font-mono-num text-2xl mt-2">{v}</p></div>;

export default function Sales() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/c/property_sales", { params: { sort: "-created_at" } }).then((r) => setRows(r.data)); }, []);
  const add = async () => {
    try { const r = await api.post("/admin/c/property_sales", { condition: "listo", agent_mode: "solo", documents: [] }); setRows((x) => [r.data, ...x]); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async (id, patch) => {
    try { const r = await api.put(`/admin/c/property_sales/${id}`, patch); setRows((x) => x.map((y) => (y.id === id ? r.data : y))); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta venta y su registro?")) return;
    try { await api.delete(`/admin/c/property_sales/${id}`); setRows((x) => x.filter((y) => y.id !== id)); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = rows.filter((r) => !q || [r.property, r.owner, r.buyer_name, r.sector, r.other_agent].some((v) => (v || "").toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-6" data-testid="sales-page">
      <PageTitle title="Venta de Propiedades" subtitle="Registra cada venta y sube los documentos que te envíen. Todo se guarda solo al salir de cada casilla." />
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Stat l="Ventas registradas" v={rows.length} tid="sales-count" />
        <Stat l="Total vendido" v={usd(rows.reduce((a, r) => a + (r.price || 0), 0))} tid="sales-total" />
        <Stat l="Total comisiones" v={usd(rows.reduce((a, r) => a + (r.commission_total || 0), 0))} tid="sales-commissions" />
        <Stat l="Con otro agente" v={rows.filter((r) => r.agent_mode === "otro").length} tid="sales-shared" />
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por propiedad, propietario, comprador, sector o agente" className={`${box} h-11 sm:flex-1`} data-testid="sales-search" />
        <button onClick={add} className="h-11 rounded-full bg-[#FF3333] hover:bg-[#E62E2E] text-white px-6 font-semibold inline-flex items-center gap-2 justify-center" data-testid="sales-add-btn"><Plus size={16} />Nueva venta</button>
      </div>
      {shown.map((r, i) => <SaleCard key={r.id} r={r} n={shown.length - i} save={save} del={del} />)}
      {!shown.length && <div className="text-center text-slate-400 py-16 bg-white rounded-2xl border border-dashed border-slate-300" data-testid="sales-empty"><BadgeDollarSign className="mx-auto mb-2" />{rows.length ? "Ninguna venta coincide con la búsqueda." : "Aún no hay ventas. Pulsa \"Nueva venta\" para registrar la primera."}</div>}
    </div>
  );
}
